import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import CTABand from '../components/CTABand';
import { Reveal } from '../components/motion';
import { IMAGES, localBusinessSchema, breadcrumbSchema } from '../data/site';

const CATEGORIES = ['All', 'HVAC', 'Plumbing', 'Excavation', 'Our Team'];

const ITEMS = [
  { src: IMAGES.teamVan, cat: 'Our Team', caption: 'The Crew & The Van', alt: 'B.E. Holbrooks Co. service team with their American Standard branded van in Stanfield, NC' },
  { src: IMAGES.techAmericanStandard, cat: 'HVAC', caption: 'American Standard Platinum Service', alt: 'B.E. Holbrooks Co. technician servicing an American Standard Platinum variable-speed air conditioner in Stanfield, NC' },
  { src: IMAGES.techGauges, cat: 'HVAC', caption: 'Refrigerant Diagnostics', alt: 'Technician checking refrigerant gauges on a residential heat pump near Stanfield, NC' },
  { src: IMAGES.acRepair, cat: 'HVAC', caption: 'Air Conditioning Service', alt: 'Air conditioning maintenance and repair service in the Stanfield, NC area' },
  { src: IMAGES.plumbing, cat: 'Plumbing', caption: 'Residential Plumbing Repair', alt: 'Residential plumbing repair and fixture work by B.E. Holbrooks Co. in Stanfield, NC' },
  { src: IMAGES.excavation, cat: 'Excavation', caption: 'Excavation & Trenching', alt: 'Excavation equipment trenching for an underground water line project near Stanfield, NC' },
  { src: IMAGES.storefront, cat: 'Our Team', caption: 'Our Stanfield Storefront', alt: 'B.E. Holbrooks Co. hardware and garden store at 204 West Stanly Street in Stanfield, NC' },
  { src: IMAGES.officeStaff, cat: 'Our Team', caption: 'The Office Team', alt: 'Office staff at B.E. Holbrooks Co. scheduling HVAC and plumbing service calls in Stanfield, NC' },
];

export default function Gallery() {
  const [filter, setFilter] = useState('All');
  const shown = filter === 'All' ? ITEMS : ITEMS.filter((i) => i.cat === filter);

  return (
    <>
      <SEO
        title="Project Gallery | HVAC & Plumbing Work in Stanfield, NC | B.E. Holbrooks Co."
        description="See real HVAC, plumbing, and excavation work from B.E. Holbrooks Co. — American Standard installations, repairs, and the team serving Stanfield, NC for over 60 years."
        path="/gallery"
        schemas={[
          localBusinessSchema,
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Gallery', path: '/gallery' }]),
        ]}
      />

      <PageHero
        eyebrow="Our Work"
        h1Lines={['Project Gallery']}
        sub="Real photos from real jobs — our technicians, our vans, and the homes and businesses we serve across Stanfield and the greater Charlotte area."
        image={IMAGES.teamVan}
        imageAlt="Project gallery of HVAC, plumbing and excavation work by B.E. Holbrooks Co. in Stanfield, NC"
        crumbs={[{ name: 'Gallery' }]}
      />

      <section className="bg-white py-16 lg:py-24" data-testid="gallery-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal className="flex flex-wrap items-center gap-2" data-testid="gallery-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
                className={`rounded-full px-5 py-2.5 font-display text-sm font-bold transition-all ${
                  filter === c
                    ? 'bg-brand text-white shadow-md shadow-brand/30'
                    : 'border border-slate-200 bg-white text-slate-600 hover:border-brand hover:text-brand'
                }`}
              >
                {c}
              </button>
            ))}
          </Reveal>

          <motion.div layout className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {shown.map((item) => (
                <motion.figure
                  layout
                  key={item.alt}
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.94 }}
                  transition={{ duration: 0.35 }}
                  className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-xl"
                  data-testid={`gallery-item-${item.cat.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={item.src}
                      alt={item.alt}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 rounded-full bg-navy/85 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur-sm">
                      {item.cat}
                    </span>
                  </div>
                  <figcaption className="p-4 font-display text-sm font-bold text-navy">{item.caption}</figcaption>
                </motion.figure>
              ))}
            </AnimatePresence>
          </motion.div>

          <Reveal className="mt-10">
            <p className="text-sm text-slate-500">
              More photos coming soon — we add new project photos as jobs are completed across Stanfield, Locust,
              Charlotte, and our other service communities.
            </p>
          </Reveal>
        </div>
      </section>

      <CTABand heading="Want Work Like This at Your Home?" testid="cta-gallery" />
    </>
  );
}
